import React from "react";
import { useNavigate } from "react-router-dom";
import PopUpPanel from "../PopUpPanel/PopUpPanel";
import { IconJsxer } from "../../helpers/IconHelper";
import { parseTimezone } from "../../helpers/TimeZones";
import { toast } from "react-toastify";
import { TOP_LEVEL_NAV_ROUTES } from "../../config/NavRoutes";
import "./StreamDetailsModal.scss";

const StreamDetailsModal = ({ stream, campaignId, onClose }) => {
    const navigate = useNavigate();
    
    if (!stream) return null;

    const timezoneData = parseTimezone(stream.timeZone);
    
    const formatDateTime = (timestamp, timezone) => {
        const date = new Date(timestamp);
        const options = { 
            weekday: 'short', 
            day: '2-digit', 
            month: 'long',
            year: 'numeric'
        };
        return date.toLocaleDateString('en-GB', options);
    };

    const formatTime = (timestamp, timezoneOffset, timezoneName) => {
        const date = new Date(timestamp + (timezoneOffset * 3600 * 1000));
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes} (${timezoneName})`;
    };

    const formatLocalTime = (timestamp) => {
        const date = new Date(timestamp);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return `${hours}:${minutes} (${timezoneName})`;
    };

    const getOBSLink = () => {
        return `${window.location.origin}/obs-feature/${campaignId}/${stream._id}/${stream.livePIN}`;
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(getOBSLink());
        toast.success('OBS link copied to clipboard!');
    };

    const openStream = () => {
        navigate(TOP_LEVEL_NAV_ROUTES.MANAGE_CAMPAIGN_STREAM.replace(':campaignId', campaignId).replace(':streamId', stream._id));
    };

    return (
        <PopUpPanel closePanelFunc={onClose} className="stream-details-modal">
            <div className="stream-details-content">
                <div className="stream-details-header">
                    <h2>{stream.name || stream.twitchHandle || 'Unnamed Stream'}</h2>
                </div>

                <div className="stream-detail-section">
                    <label>Stream type</label>
                    <div className="detail-value">{stream.demoMode ? 'Live test' : 'Live stream'}</div>
                </div>

                <div className="stream-detail-section">
                    <label>Day</label>
                    <div className="detail-value">{formatDateTime(stream.startTime, timezoneData.offset)}</div>
                </div>

                <div className="stream-detail-section">
                    <label>Time</label>
                    <div className="detail-value time-display">
                        <div>{formatTime(stream.startTime, timezoneData.offset, stream.timeZone)} - {formatTime(stream.endTime, timezoneData.offset, stream.timeZone)}</div>
                        <div>{formatLocalTime(stream.startTime)} - {formatLocalTime(stream.endTime)}</div>
                    </div>
                </div>

                <div className="stream-detail-section">
                    <label>OBS link</label>
                    <div className="obs-link-row">
                        <input 
                            type="text" 
                            value={getOBSLink()} 
                            readOnly 
                            className="obs-link-input"
                        />
                        <button className="copy-link-button" onClick={copyToClipboard}>
                            {IconJsxer.GetIcon(IconJsxer.ICONS.link)}
                            <span>Copy link</span>
                        </button>
                    </div>
                </div>

                <div className="stream-detail-section">
                    <label>Streamers</label>
                    <div className="streamers-list-detail">
                        {stream.channels && stream.channels.length > 0 ? (
                            stream.channels.map((channel, index) => (
                                <div key={index} className="streamer-detail-item">
                                    {channel.profilePic ? (
                                        <img src={channel.profilePic} alt={channel.twitchHandle} className="streamer-avatar-detail" />
                                    ) : (
                                        <div className="streamer-avatar-placeholder-detail"></div>
                                    )}
                                    <a 
                                        href={`https://www.twitch.tv/${channel.twitchHandle}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="streamer-name-link"
                                    >
                                        {channel.twitchHandle}
                                    </a>
                                </div>
                            ))
                        ) : (
                            <div className="no-streamers">No streamers added</div>
                        )}
                    </div>
                </div>

                <button className="open-stream-button" onClick={openStream}>
                    Open stream
                </button>
            </div>
        </PopUpPanel>
    );
};

export default StreamDetailsModal;
